import { useEstabelecimento } from "@/contexts/cadastro/estabelecimento";
import { PessoaInspecao, Proprietario } from "@/contexts/cadastro/pessoa";
import {
  Formulario,
  SecaoQuestionario,
  useFormulario,
  useFormularioRespostaControle,
} from "@/contexts/fiscalizacao/formulario";
import { RelatorioAMFormValues } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAMFormValues";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { App, Col, Form, FormInstance, Input, Row, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

export function FormDadosGeraisRelatorioAM({
  form,
  setSecoesQuestionario,
  setIsLoadingQuestionario,
  isEdit = false,
}: {
  form: FormInstance<RelatorioAMFormValues>;
  setSecoesQuestionario: React.Dispatch<
    React.SetStateAction<SecaoQuestionario[]>
  >;
  setIsLoadingQuestionario: React.Dispatch<React.SetStateAction<boolean>>;
  isEdit?: boolean;
}) {
  const { isOnline } = useNetworkStatus();
  const { estabelecimentoOptions, listaEstabelecimentos } =
    useEstabelecimento();
  const { listaFormularios, getFormulariosForPerfil } = useFormulario();
  const { getFormulariosRespostaControle } = useFormularioRespostaControle();
  const estabelecimentoId = Form.useWatch(["estabelecimento", "id"], form);
  const { message, notification } = App.useApp();
  const [proprietarios, setProprietarios] = useState<Proprietario[]>([]);
  const [pessoasInspecao, setPessoasInspecao] = useState<PessoaInspecao[]>([]);
  const [formulariosOptions, setFormulariosOptions] = useState<Formulario[]>(
    []
  );

  const handleChangeFormulario = async (value: number) => {
    const formularioSelecionado = listaFormularios.find((f) => f.id === value);

    setSecoesQuestionario([]);
    form.setFieldsValue({ questionarios: [] });

    if (!formularioSelecionado) {
      return;
    }

    const estabelecimentoSelecionado = listaEstabelecimentos.find(
      (e) => e.id === estabelecimentoId
    );

    if (!estabelecimentoSelecionado) {
      return;
    }

    try {
      message.loading({
        content: "Carregando perguntas...",
        key: "loadingPerguntas",
      });

      setIsLoadingQuestionario(true);

      const formulariosRespostaControle = await getFormulariosRespostaControle(
        estabelecimentoSelecionado.endereco!.municipio.id
      );

      if (!isOnline && formulariosRespostaControle.length === 0) {
        message.info({
          content:
            "Não há dados de formulários de controle de respostas disponíveis offline para a localidade desse estabelecimento.",
        });
      }

      const secoes = formularioSelecionado.perguntas.reduce(
        (acc, pergunta) => {
          if (!pergunta.pergunta || !pergunta.secao) {
            return acc;
          }

          const secaoId = pergunta.secao.id;

          if (
            pergunta.pergunta.visivel === false ||
            pergunta.pergunta.ativo === false
          ) {
            return acc;
          }

          if (!acc[secaoId]) {
            acc[secaoId] = {
              secao: pergunta.secao.nome ?? "",
              perguntas: [],
            };
          }

          const perguntaFormControle = formulariosRespostaControle.find(
            (item) =>
              item.perguntaId === pergunta.pergunta?.id &&
              item.secaoId === secaoId &&
              item.formularioId === formularioSelecionado.id &&
              item.estabelecimentoId === estabelecimentoId
          );

          if (perguntaFormControle) {
            const diasDesdeUltimaResposta = dayjs().diff(
              dayjs(perguntaFormControle.dataUltimaResposta),
              "day"
            );

            if (diasDesdeUltimaResposta < (pergunta.dias ?? 0)) {
              return acc; // Pula esta pergunta
            }
          }

          acc[secaoId].perguntas.push({
            id: pergunta.pergunta.id,
            ordem: pergunta.ordem,
            nome: pergunta.pergunta.nome,
            dica: pergunta.pergunta.dica,
            tipoResposta: pergunta.pergunta.tipoResposta,
            peso: pergunta.peso,
            dias: pergunta.dias,
            alternativas: pergunta.pergunta.alternativas,
            visivel: pergunta.pergunta.visivel,
            ativo: pergunta.pergunta.ativo,
          });

          return acc;
        },
        {} as Record<
          number,
          { secao: string; perguntas: SecaoQuestionario["perguntas"] }
        >
      );

      const secoesArray = Object.values(secoes);

      const secoesFiltradas = secoesArray.filter(
        (secao) => secao.perguntas.length > 0
      );
      setSecoesQuestionario(secoesFiltradas);

      // Inicializa o array de questionários no formulário
      const questionariosIniciais: RelatorioAMFormValues["questionarios"] = [];
      secoesFiltradas.forEach((secao) => {
        secao.perguntas.forEach((pergunta) => {
          questionariosIniciais.push({
            pergunta: { id: pergunta.id },
            alternativa: undefined,
            observacao: "",
            anexo: [],
          });
        });
      });

      message.destroy("loadingPerguntas");
      setIsLoadingQuestionario(false);

      form.setFieldsValue({
        questionarios: questionariosIniciais,
      });
    } catch (error) {
      message.destroy("loadingPerguntas");
      setIsLoadingQuestionario(false);
      const messageError = formatMessageError(error);

      notification.error({
        message: "Não foi possivel carregar perguntas",
        description: messageError,
      });
    }
  };

  useEffect(() => {
    if (isEdit) {
      const estabelecimentoSelecionado = listaEstabelecimentos.find(
        (e) => e.id === estabelecimentoId
      );

      setProprietarios(estabelecimentoSelecionado?.sociosProprietarios || []);
      setPessoasInspecao(estabelecimentoSelecionado?.pessoasInspecao || []);

      const formulariosForPerfil = getFormulariosForPerfil(estabelecimentoId);

      setFormulariosOptions(formulariosForPerfil);
    }
  }, [proprietarios, estabelecimentoId, isEdit]);

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            label="Estabelecimento"
            name={["estabelecimento", "id"]}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecione o Estabelecimento"
              showSearch
              allowClear
              optionFilterProp="label"
              options={estabelecimentoOptions}
              onChange={(value) => {
                form.resetFields([
                  "questionarios",
                  "formulario",
                  "inspetor",
                  "proprietario",
                ]);
                setSecoesQuestionario([]);

                const estabelecimentoSelecionado = listaEstabelecimentos.find(
                  (e) => e.id === value
                );

                setProprietarios(
                  estabelecimentoSelecionado?.sociosProprietarios || []
                );
                setPessoasInspecao(
                  estabelecimentoSelecionado?.pessoasInspecao || []
                );

                const formulariosForPerfil = getFormulariosForPerfil(value);

                setFormulariosOptions(formulariosForPerfil);
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            label="Formulário"
            name={["formulario", "id"]}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecione o Formulário"
              showSearch
              allowClear
              optionFilterProp="label"
              options={formulariosOptions.map((formulario) => ({
                label: formulario.nome,
                value: formulario.id,
              }))}
              onChange={handleChangeFormulario}
              disabled={!estabelecimentoId}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            label="Proprietário"
            name={["proprietario", "id"]}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecione o Proprietário"
              showSearch
              allowClear
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              optionFilterProp="label"
              options={proprietarios.map((proprietario) => ({
                label: proprietario.nome,
                value: proprietario.id,
              }))}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            label="Inspetor/Monitor"
            name={["inspetor", "id"]}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecione o Inspetor/Monitor"
              showSearch
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              optionFilterProp="label"
              allowClear
              options={pessoasInspecao.map((pessoa) => ({
                label: pessoa.nomePessoa,
                value: pessoa.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            label="Responsável Técnico"
            name={"responsavelTecnico"}
            rules={[{ required: true }]}
          >
            <Input placeholder="informe o Responsável Técnico" />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item label="Observação" name={"observacao"}>
            <Input.TextArea placeholder="Observação" rows={2} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}
